import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import { externalAuth, clearPendingExternalAuth, readPendingExternalAuth,
  type ExternalProvider, type ExternalIntent, type ExternalStep, type PendingExternalAuth } from '@/services/externalAuth';
import AgreementModal from './AgreementModal';
import { AGREEMENTS } from '../agreementContent';

const names: Record<ExternalProvider, string> = { x: 'X', telegram: 'Telegram', apple: 'Apple' };
interface Props {
  provider: ExternalProvider;
  intent?: ExternalIntent;
  email?: string;
  handoff?: string;
  flowId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ExternalAuthFlow({ provider, intent = 'login', email, handoff, flowId, onClose, onSuccess }: Props) {
  const { accessToken, acceptExternalSession } = useAuth();
  const [step, setStep] = useState<ExternalStep | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const pending = useRef<PendingExternalAuth | null>(null);
  const alive = useRef(true);
  const callbacks = useRef({ onClose, onSuccess, acceptExternalSession });
  callbacks.current = { onClose, onSuccess, acceptExternalSession };

  function showError(value: unknown) {
    if (!alive.current) return;
    setError(value instanceof ApiError ? value.message : value instanceof Error ? value.message : '身份验证失败，请重新开始');
    setWaiting(false);
  }
  async function apply(next: ExternalStep) {
    if (!alive.current) return;
    setCode('');
    if (next.status === 'authenticated') {
      await callbacks.current.acceptExternalSession(next.auth);
      clearPendingExternalAuth();
      callbacks.current.onSuccess();
    } else if (next.status === 'bound' || next.status === 'unbound') {
      clearPendingExternalAuth();
      callbacks.current.onSuccess();
    } else if (next.status === 'revocation_pending') {
      clearPendingExternalAuth();
      setStep(next);
    } else if (next.status === 'canceled' || next.status === 'failed' || next.status === 'already_completed') {
      clearPendingExternalAuth();
      setError('本次授权未完成，请重新开始');
      setStep(null);
    } else {
      setStep(next);
      setWaiting(false);
    }
  }
  useEffect(() => {
    alive.current = true;
    const kickoff = window.setTimeout(() => {
      void (async () => {
        try {
          if (handoff || flowId) {
            const stored = readPendingExternalAuth();
            if (!stored || stored.provider !== provider || stored.flow_id !== flowId ||
                !handoff || !/^[A-Za-z0-9_-]{32,128}$/.test(handoff)) throw new Error('回跳与原授权不匹配，请重新开始');
            pending.current = stored;
            await externalAuth.closeBrowser();
            await apply(await externalAuth.continue(stored, handoff));
            return;
          }
          const result = await externalAuth.start(provider, intent, accessToken, email);
          pending.current = result.pending;
          if (result.start.step) { await apply(result.start.step); return; }
          if (intent === 'unlink') { await apply(await externalAuth.complete(result.pending, {})); return; }
          if (result.start.transport === 'native_credential') {
            if (!result.start.nonce) throw new Error('Apple 授权配置缺少 nonce');
            await apply(await externalAuth.exchangeApple(result.pending, result.start.state, result.start.nonce));
            return;
          }
          setWaiting(true);
          await externalAuth.openBrowser(result.start);
        } catch (value) {
          if ((value as { code?: string }).code === 'APPLE_CANCELLED') close();
          else showError(value);
        } finally { if (alive.current) setBusy(false); }
      })();
    }, 0);
    return () => { alive.current = false; window.clearTimeout(kickoff); };
    // One mounted component owns one attempt; callback refs avoid starting another attempt on rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function submit(action: () => Promise<ExternalStep>) {
    if (busy || !pending.current) return;
    setBusy(true); setError('');
    try { await apply(await action()); } catch (value) { showError(value); }
    finally { if (alive.current) setBusy(false); }
  }
  async function sendEmail() {
    if (!pending.current || busy || cooldown) return;
    setBusy(true); setError('');
    try {
      const result = await externalAuth.sendEmail(pending.current);
      if (alive.current) { setSent(true); setCooldown(result.rate_seconds || 60); }
    } catch (value) { showError(value); }
    finally { if (alive.current) setBusy(false); }
  }
  function close() {
    const current = pending.current;
    clearPendingExternalAuth();
    void externalAuth.closeBrowser();
    if (current) void externalAuth.cancel(current).catch(() => undefined);
    onClose();
  }
  const label = names[provider];
  const codeStep = step?.status === 'needs_email' || step?.status === 'needs_totp';
  return <div role="dialog" aria-modal="true" aria-label={`${label} 账号验证`}
    className="fixed inset-0 z-[100] overflow-y-auto bg-white px-6"
    style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 72px)', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 32px)' }}>
    <div className="mx-auto max-w-sm">
      <button type="button" onClick={close} className="mb-8 min-h-11 text-gray-600">返回</button>
      <h1 className="mb-3 text-xl font-semibold text-gray-900">{intent === 'unlink' ? `解除 ${label} 绑定` : `使用 ${label} 继续`}</h1>
      {busy && <p role="status" className="my-5 text-gray-600">正在验证身份…</p>}
      {waiting && <p role="status" className="my-5 text-gray-600">请在授权页面完成操作，然后返回 ForexMind。关闭授权页面不会创建平台账号。</p>}
      {error && <p role="alert" className="my-5 text-sm text-red-600">{error}</p>}
      {error && !step && <button type="button" onClick={close} className="min-h-11 text-indigo-600">返回登录页</button>}
      {step?.status === 'needs_account_choice' && <div className="space-y-4">
        <p className="text-sm text-gray-600">如已有 ForexMind 账号，请选择关联并验证原账号邮箱；平台不会按 Apple 邮箱自动合并账号。</p>
        <button type="button" disabled={busy} onClick={() => void submit(() => externalAuth.selectAccount(pending.current!, 'new'))} className="block h-12 w-full rounded-xl bg-indigo-600 text-white">创建新账号</button>
        <button type="button" disabled={busy} onClick={() => void submit(() => externalAuth.selectAccount(pending.current!, 'existing'))} className="block h-12 w-full rounded-xl border border-gray-300">关联已有账号</button>
      </div>}
      {step?.status === 'needs_email_input' && <div className="space-y-4">
        <p className="text-sm text-gray-600">请输入平台登录邮箱。若邮箱已有账号，下一步须验证该邮箱和已启用的双重验证。</p>
        <input type="email" autoComplete="email" aria-label="平台登录邮箱" value={inputEmail} onChange={event => setInputEmail(event.target.value)} className="h-12 w-full rounded-xl border px-3" />
        <button type="button" disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputEmail.trim())}
          onClick={() => void submit(() => externalAuth.selectEmail(pending.current!, inputEmail.trim()))}
          className="h-12 w-full rounded-xl bg-indigo-600 text-white disabled:bg-gray-300">继续</button>
      </div>}
      {step?.status === 'needs_terms' && <AgreementModal docs={AGREEMENTS} onCancel={close} busy={busy} error={error}
        onConfirm={() => void submit(() => externalAuth.complete(pending.current!, { agreed_terms: true, terms_version: step.terms_version }))} />}
      {codeStep && <div className="space-y-4">
        <p className="text-sm text-gray-600">{step.status === 'needs_email' ? `请验证平台邮箱 ${step.email_masked}，以保留原账号和跟随关系。` : '请输入身份验证器中的 6 位验证码。'}</p>
        <input type="text" inputMode="numeric" autoComplete="one-time-code" aria-label="输入 6 位验证码" maxLength={6}
          value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          className="h-12 w-full rounded-xl border px-3" />
        <button type="button" disabled={busy || code.length !== 6 || (step.status === 'needs_email' && !sent)}
          onClick={() => void submit(() => externalAuth.complete(pending.current!, step.status === 'needs_email' ? { otp: code } : { totp_code: code }))}
          className="h-12 w-full rounded-xl bg-indigo-600 text-white disabled:bg-gray-300">验证并继续</button>
        {step.status === 'needs_email' && <button type="button" disabled={busy || cooldown > 0} onClick={() => void sendEmail()} className="min-h-11 text-indigo-600 disabled:text-gray-400">
          {cooldown ? `${cooldown}s 后重新发送` : sent ? '重新发送' : '发送验证码'}</button>}
      </div>}
      {step?.status === 'revocation_pending' && <div className="space-y-4">
        <p role="status" className="text-sm text-gray-600">平台已停止使用该 Apple 身份，Apple 授权撤销正在处理；您的平台账号和交易记录保留。</p>
        <button type="button" onClick={onSuccess} className="min-h-11 text-indigo-600">返回安全中心</button>
      </div>}
    </div>
  </div>;
}
